import React, { useEffect, useContext as useContextCore } from 'react'
import type { UseListWithSelection, UseListResult } from '@ztechapps/common.hook'
import { useListWithSelection } from '@ztechapps/common.hook'
import type { InventoryBySerialNumber } from '@ztechapps/common.contract'
import { useInventoryBySerialNumberList } from '@ztechapps/inventorybyserialnumber.common'
import type { SerialNumberSelectListProps } from './types'

export type SerialNumberSelectListContextType = {
  inventoryBySerialNumberList: UseListResult<InventoryBySerialNumber>
  listWithSelection: UseListWithSelection<InventoryBySerialNumber>
  maxSelection?: number
  requiredQty?: number
  onSelectionChange?: (selectedItems: InventoryBySerialNumber[]) => void
  disabled?: boolean
}

const context = React.createContext<SerialNumberSelectListContextType>({} as SerialNumberSelectListContextType)
const CoreProvider = context.Provider

export const SerialNumberSelectListProvider = ({
  children,
  filter,
  onSelectionChange,
  maxSelection,
  requiredQty,
  disabled,
}: SerialNumberSelectListProps & { children: React.ReactNode }) => {
  const inventoryBySerialNumberList = useInventoryBySerialNumberList({
    filter,
  })

  const listWithSelection = useListWithSelection(inventoryBySerialNumberList.list)

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(listWithSelection.selectedList)
    }
    // `onSelectionChange` deliberately excluded — this is a public prop of a shared library
    // component with call sites across the estate; a caller passing an inline (unmemoized)
    // callback that updates its own state on each notification would re-trigger this effect on
    // every one of its own re-renders, looping. Fire only when the selection itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listWithSelection.selectedList])

  return (
    <CoreProvider
      value={{
        inventoryBySerialNumberList,
        listWithSelection,
        maxSelection,
        requiredQty,
        onSelectionChange,
        disabled,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useSerialNumberSelectListContext = () => useContextCore(context)
